import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Wrench, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

type TabKey = 'cost-per-vehicle' | 'cost-per-km' | 'recurring-issues' | 'parts-spend' | 'vendor-spend' | 'pending-payments' | 'verification-backlog';

const TAB_LABELS: Record<TabKey, string> = {
  'cost-per-vehicle': 'Cost / vehicle',
  'cost-per-km': 'Cost / km',
  'recurring-issues': 'Recurring issues',
  'parts-spend': 'Top parts',
  'vendor-spend': 'Top vendors',
  'pending-payments': 'Pending payments',
  'verification-backlog': 'Verification backlog',
};

export default function MaintenanceReportsPage() {
  const [tab, setTab] = useState<TabKey>('cost-per-vehicle');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function go() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (from) params.set('from', from);
        if (to)   params.set('to', to);
        const r = await apiClient.get<Array<Record<string, unknown>>>(`/api/v1/maintenance/reports/${tab}?${params}`);
        if (!cancelled) { setRows(Array.isArray(r.data) ? r.data : []); setError(null); }
      } catch (e) { if (!cancelled) setError(extractErrorMessage(e)); }
      finally { if (!cancelled) setLoading(false); }
    }
    go();
    return () => { cancelled = true; };
  }, [tab, from, to]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Maintenance reports</div>
          <div className="w-24" />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        <div className="flex gap-2 items-end flex-wrap">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" /></div>
        </div>
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {(Object.keys(TAB_LABELS) as TabKey[]).map(k => (
            <button key={k} onClick={() => setTab(k)}
              className={'px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ' + (tab === k ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {TAB_LABELS[k]}
            </button>
          ))}
        </div>

        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        : rows.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No data for this report.</div>
        : <GenericTable rows={rows} />}
      </main>
    </div>
  );
}

function GenericTable({ rows }: { rows: Array<Record<string, unknown>> }) {
  if (rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  return (
    <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
          <tr>{cols.map(c => <th key={c} className="text-left px-3 py-2">{c.replace(/_/g, ' ')}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border last:border-b-0">
              {cols.map(c => <td key={c} className="px-3 py-2 text-xs">{String(r[c] ?? '—')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
